// src/lib/mnagers/user/types/settings.ts

/**
 * Email display modes
 * @example 'TABLE' for a table display, 'MOSAIC' for a grid display
 */
type EmailDisplayMode = "TABLE" | "MOSAIC";

/**
 * Enum to define languages ISO codes for fr and en
 */
enum Language {
    FRENCH = "fr-FR",
    ENGLISH = "en-US",
}

/**
 * Dashboard widgets configuration
 */
type DashboardWidgetSettings = {
    /** Calendar display */
    showCalendar: boolean;
    /** General task list display */
    showTodo: boolean;
    /** SmartLabel repartition of mails display */
    showRepartition: boolean;
    /** Tips of the day display */
    showTipofTheDay: boolean;
    /** Daily mood display */
    showDailyMood: boolean;
    /** Carbon impact display */
    showCOImpact: boolean;
    /** Notepad display */
    showNotepad: boolean;
    /** Weather display */
    showMeteo: boolean;
};

/**
 * User display preferences
 */
type DisplayPreferences = {
    /** Email display mode (table or mosaic) */
    emailDisplayMode: EmailDisplayMode;
    /** Dashboard widgets configuration */
    dashboardWidgets: DashboardWidgetSettings;
};

/**
 * Visual theme configuration
 */
type Theme = {
    /** Indicates if dark mode is enabled */
    darkMode: boolean;
    /** Theme image reference (for predefined themes) */
    image: string;
    /** Background color in hex format (#RRGGBB) */
    backgroundColor: string;
    /** Custom background image URL */
    backgroundImageUrl: string;
    /** Indicates if a custom image is used instead of a solid background */
    useCustomImage: boolean;
};

/**
 * Notification settings
 */
type NotificationSettings = {
    /** Indicates if push notifications are enabled */
    activatePushNotifications: boolean;
    /** Indicates if email notifications are enabled */
    getUnreadDailyNotification: boolean;
    /** Indicates if unread email notifications are enabled */
    getUrgentMessageNotification: boolean;
    /** Indicates if daily summary notifications are enabled */
    getDailySummaryNotification: boolean;
};

/**
 * Global user settings
 */
type Settings = {
    /** Visual theme configuration */
    theme: Theme;
    /** Display preferences */
    displayPreferences: DisplayPreferences;
    /** Language preference */
    preferredLanguage: Language;
    /** Custom signature */
    customSignature?: string;
    /** Notification preferences */
    notificationPreferences: NotificationSettings;
};

/**
 * AB#1834 — Mise à jour partielle des settings (PATCH v1/me/userProfile).
 * Miroir du type SettingsUpdate backend : chaque bloc imbriqué est lui-même partiel,
 * la fusion champ à champ avec les settings existants est faite côté Api-Backend.
 */
type SettingsUpdate = {
    /** Visual theme configuration */
    theme?: Partial<Theme>;
    /** Display preferences */
    displayPreferences?: {
        emailDisplayMode?: EmailDisplayMode;
        dashboardWidgets?: Partial<DashboardWidgetSettings>;
    };
    /** Language preference */
    preferredLanguage?: Language;
    /** Custom signature — chaîne vide acceptée (efface la signature) */
    customSignature?: string;
    /** Notification preferences */
    notificationPreferences?: Partial<NotificationSettings>;
};

enum MailvistaUrls {
    OFFICIALSITE = "website",
    CONDITIONGENERALESUTILISATION = "cgu",
    CONDITIONGENERALESUTILISATIONPARTICULIER = "cgu particuliers",
    CONDITIONGENERALESUTILISATIONENTREPRISE = "cgu entreprise",
    POLITIQUECONFIDENTIALITE = "politique de confidentialité",
    ONBOARDINGVIDEO = "video d accueil",
    MENTIONLEGALEWEB = "mentions légales web",
    MENTIONLEGALEAPPLICATION = "mentions légales application",
}

type Url = {
    id: MailvistaUrls;
    url: string;
    title: string;
};

// Exports
export type {
    EmailDisplayMode,
    DashboardWidgetSettings,
    DisplayPreferences,
    Theme,
    NotificationSettings,
    Settings,
    SettingsUpdate,
    Url,
};

export { Language, MailvistaUrls };
